import { authorityConfig, type AuthorityLevel } from "@iamu/shared";
import {
  LegislationIcon,
  RegulationIcon,
  StandardIcon,
  CompanyPolicyIcon,
  GuidelineIcon,
  InformationalIcon,
} from "@/components/icons";
import { Chip, type ChipTone } from "@/components/ui/chip";
import { cn } from "@/lib/utils";

const customIconMap = {
  LegislationIcon,
  RegulationIcon,
  StandardIcon,
  CompanyPolicyIcon,
  GuidelineIcon,
  InformationalIcon,
} as const;

interface AuthorityBadgeProps {
  /** Unknown values render as a neutral chip with the raw value */
  level: AuthorityLevel | string;
  className?: string;
  tone?: ChipTone;
}

/**
 * Authority level of a knowledge source. The level's icon and word carry
 * the meaning. Legislation and regulation are legally binding hard constraints
 * and receive primary emphasis.
 */
export function AuthorityBadge({
  level,
  className,
  tone,
}: AuthorityBadgeProps) {
  const config = authorityConfig[level as AuthorityLevel];
  const Icon = config?.customIcon ? customIconMap[config.customIcon] : null;
  const resolvedTone =
    tone ??
    (level === "legislation" || level === "regulation" ? "primary" : "neutral");

  return (
    <Chip tone={resolvedTone} className={cn("text-xs font-medium", className)}>
      {Icon && <Icon aria-hidden="true" />}
      {config?.label ?? (level || "Unknown")}
    </Chip>
  );
}
